import { safeNext } from '@crow/service-kit/web/session';
import { PendingButton } from '@crowdex/ui/components/pending-button';
import { useState } from 'react';
import { RuntimeServiceError, runtimeServices } from '@/lib/runtime-services';

/** Where to go once signed in: the page that asked for it (`?next=/path`), or the app. */
export function afterSignIn(search: string): string {
  return safeNext(new URLSearchParams(search).get('next'), '/desktop');
}

/** What the sign-in page says of a sign-in that came back refused (`?error=`). */
export function signInError(search: string): string | null {
  const error = new URLSearchParams(search).get('error');
  if (error === null) return null;
  if (error === 'local_profile')
    return 'This Crowdex runs on this computer: open it from the Crowdex app or the link `crowdex serve` printed.';
  if (error === 'unverified_email')
    return 'Verify the e-mail address of your Crowlingo account, then sign in again.';
  // A silent sign-in again (a service added since the last one) that needs the user's credentials.
  if (error === 'login_required' || error === 'interaction_required')
    return 'Sign in again to keep using your Crowlingo services.';
  if (error === 'no_organization' || error === 'no_email')
    return 'Your Crowlingo account is not set up for Crowdex yet. Ask its administrator.';
  return 'The sign-in did not go through. Try again.';
}

/**
 * Signs in with a Crowlingo account: the browser goes to Crowlingo's sign-in (Zitadel) and comes
 * back to the page that asked for it. Nothing happens until it is clicked.
 */
export function SignInButton() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(() => signInError(window.location.search));

  async function signIn() {
    setPending(true);
    setError(null);
    try {
      window.location.assign(await runtimeServices().signIn(afterSignIn(window.location.search)));
    } catch (cause) {
      setError(
        cause instanceof RuntimeServiceError
          ? cause.message
          : 'Crowdex could not reach the server. Try again.',
      );
      setPending(false);
    }
  }

  return (
    <div className="space-y-4">
      {error && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}
      <PendingButton
        className="w-full"
        onClick={() => void signIn()}
        pending={pending}
        pendingLabel="Redirection…"
        type="button"
      >
        Se connecter avec Crowlingo
      </PendingButton>
    </div>
  );
}
